import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { UsuarioService } from './usuario.service';
import { SessaoService } from './sessao.service';

@Injectable({
  providedIn: 'root'
})
export class TokenValidatorService {
  private checkInterval: any;

  constructor(
    private router: Router,
    private usuarioService: UsuarioService,
    private sessao: SessaoService
  ) {}

  /**
   * Verifica se o token está expirado
   */
  isTokenExpired(token?: string): boolean {
    if (!token) {
      token = localStorage.getItem('token');
    }

    if (!token) {
      return true;
    }

    try {
      const decoded: any = jwtDecode(token);
      
      // O token JWT tem um campo 'exp' com o timestamp de expiração
      if (!decoded.exp) {
        return true;
      }

      // Verifica se o token está expirado (exp está em segundos, Date.now() em ms)
      const expirationDate = decoded.exp * 1000;
      const now = Date.now();
      
      // Adiciona uma margem de 60 segundos para evitar problemas de sincronização
      return now >= (expirationDate - 60000);
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return true;
    }
  }

  /**
   * Sem token (saiu, inclusive em outra aba): vai para o login. Token vencendo: renova em segundo
   * plano pelo refresh token; quem leva ao login, se a sessão for recusada, é o SessaoService.
   */
  validateTokenAndRedirect(): boolean {
    const token = localStorage.getItem('token');

    if (!token) {
      this.handleExpiredToken();
      return false;
    }

    if (this.sessao.precisaRenovar()) {
      this.sessao.renovar().catch(() => { /* sem rede: tenta no próximo ciclo */ });
    }

    return true;
  }

  /**
   * Lida com token expirado
   */
  private handleExpiredToken(): void {
    this.usuarioService.logout();
    
    const currentPath = window.location.pathname;
    
    // Rotas públicas que não devem redirecionar para login
    const publicRoutes = [
      '/autenticacao/login',
      '/autenticacao/criar-senha-usuario',
      '/autenticacao/esqueci-minha-senha'
    ];
    
    const isPublicRoute = publicRoutes.some(route => currentPath.includes(route));
    
    if (!isPublicRoute) {
      this.router.navigateByUrl('/autenticacao/login');
    }
  }

  /**
   * Inicia verificação periódica do token
   */
  startTokenValidation(intervalMinutes: number = 5): void {
    // Para qualquer verificação anterior
    this.stopTokenValidation();
    
    // Verifica imediatamente
    this.validateTokenAndRedirect();
    
    // Configura verificação periódica (padrão: a cada 5 minutos)
    this.checkInterval = setInterval(() => {
      this.validateTokenAndRedirect();
    }, intervalMinutes * 60 * 1000);
  }

  /**
   * Para a verificação periódica
   */
  stopTokenValidation(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Retorna o tempo restante do token em minutos
   */
  getTokenRemainingTime(): number {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return 0;
    }

    try {
      const decoded: any = jwtDecode(token);
      
      if (!decoded.exp) {
        return 0;
      }

      const expirationDate = decoded.exp * 1000;
      const now = Date.now();
      const remainingMs = expirationDate - now;
      
      return Math.max(0, Math.floor(remainingMs / 60000)); // Retorna em minutos
    } catch (error) {
      return 0;
    }
  }

  /**
   * Exibe alerta quando o token está prestes a expirar
   */
  setupExpirationWarning(warningMinutes: number = 10): void {
    const checkWarning = () => {
      const remainingTime = this.getTokenRemainingTime();
      
      // Se resta menos tempo que o warning e mais que 0
      if (remainingTime > 0 && remainingTime <= warningMinutes) {
        // Aqui você pode mostrar um modal ou notificação
        console.warn(`Token expirará em ${remainingTime} minutos`);
        
        // Opcional: mostrar confirmação para renovar sessão
        if (confirm(`Sua sessão expirará em ${remainingTime} minutos. Deseja continuar?`)) {
          // Aqui poderia implementar um refresh token se disponível
          // Ou apenas resetar o timer de inatividade
        }
      }
    };

    // Verifica a cada minuto
    setInterval(checkWarning, 60000);
  }
}